import type { ComponentType } from 'react';
import { useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { LucideIcon } from 'lucide-react';
import { ArrowLeftRight, ChevronLeft, X, Minus } from 'lucide-react';
import type { LayoutPanel, AppRegistry, PanelComponents, PanelComponentEntry } from './types';
import { useWorkspace } from './WorkspaceContext';
import { Card } from '@/components/ui/card';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
} from '@/components/ui/context-menu';

type PanelSlotProps = {
  panel: LayoutPanel;
  registry: AppRegistry;
  components?: PanelComponents;
  interactive: boolean;
  locked: boolean;
  noHeader: boolean;
  isLastPanel: boolean;
  onSetApp: (panelId: string, appType: string | null) => void;
  onSplit: (panelId: string, direction: 'horizontal' | 'vertical') => void;
  onRemove: (panelId: string) => void;
};

type PanelContextMenuProps = {
  panelId: string;
  hasApp: boolean;
  isLastPanel: boolean;
  onSplit: (panelId: string, direction: 'horizontal' | 'vertical') => void;
  onRemove: (panelId: string) => void;
  onClearApp: () => void;
  children: React.ReactNode;
};

const isPanelEntry = (v: ComponentType | PanelComponentEntry): v is PanelComponentEntry =>
  typeof v === 'object' && v !== null && 'component' in v;

const PanelContextMenu = ({ panelId, hasApp, isLastPanel, onSplit, onRemove, onClearApp, children }: PanelContextMenuProps) => {
  const { swapSourceId, setSwapSourceId, maximizedPanelId, setMaximizedPanelId } = useWorkspace();
  const isMaximized = maximizedPanelId === panelId;

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onClick={() => setMaximizedPanelId(isMaximized ? null : panelId)}>
          {isMaximized ? 'Restore' : 'Maximize'}
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={() => onSplit(panelId, 'horizontal')}>Split horizontal</ContextMenuItem>
        <ContextMenuItem onClick={() => onSplit(panelId, 'vertical')}>Split vertical</ContextMenuItem>
        {hasApp && <ContextMenuItem onClick={onClearApp}>Clear app</ContextMenuItem>}
        {!isLastPanel && (
          <ContextMenuItem className="text-red-500 focus:text-red-500" onClick={() => onRemove(panelId)}>
            Remove panel
          </ContextMenuItem>
        )}
        <ContextMenuSeparator />
        {swapSourceId ? (
          <ContextMenuItem onClick={() => setSwapSourceId(null)}>Cancel swap</ContextMenuItem>
        ) : (
          <ContextMenuItem onClick={() => setSwapSourceId(panelId)}>Swap with...</ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};

const SwapOverlay = ({ panelId }: { panelId: string }) => {
  const { swapSourceId, onSwap } = useWorkspace();

  if (!swapSourceId || swapSourceId === panelId) return null;

  return (
    <div
      className="absolute inset-0 z-20 flex items-center justify-center bg-primary/10 hover:bg-primary/20 transition-colors cursor-pointer"
      onClick={() => onSwap(swapSourceId, panelId)}
    >
      <div className="flex items-center gap-2 rounded-lg bg-background/90 backdrop-blur-sm px-3 py-2 shadow-lg border border-primary/30">
        <ArrowLeftRight className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium text-primary">Swap here</span>
      </div>
    </div>
  );
};

const SwapSourceIndicator = ({ panelId }: { panelId: string }) => {
  const { swapSourceId, setSwapSourceId } = useWorkspace();

  if (swapSourceId !== panelId) return null;

  return (
    <div className="absolute inset-0 z-20 pointer-events-none ring-2 ring-inset ring-primary/50 rounded-lg">
      <div className="absolute top-2 left-2 pointer-events-auto">
        <button
          type="button"
          onClick={() => setSwapSourceId(null)}
          className="flex items-center gap-1.5 rounded-md bg-primary/90 px-2.5 py-1 text-xs font-medium text-primary-foreground shadow cursor-pointer hover:bg-primary"
        >
          <ArrowLeftRight className="h-3 w-3" />
          Swapping... click to cancel
        </button>
      </div>
    </div>
  );
};

const TrafficLights = ({ panelId, isLastPanel, onRemove, onClearApp }: { panelId: string; isLastPanel: boolean; onRemove: (panelId: string) => void; onClearApp: () => void }) => {
  const { maximizedPanelId, setMaximizedPanelId } = useWorkspace();
  const isMaximized = maximizedPanelId === panelId;

  const handleClose = useCallback(() => {
    onClearApp();
  }, [onClearApp]);

  const handleRestore = useCallback(() => {
    setMaximizedPanelId(null);
  }, [setMaximizedPanelId]);

  const handleMaximize = useCallback(() => {
    setMaximizedPanelId(panelId);
  }, [panelId, setMaximizedPanelId]);

  if (isMaximized) {
    return (
      <div className="flex items-center gap-1.5 shrink-0 ml-auto">
        <button
          type="button"
          onClick={handleRestore}
          className="group/btn h-3 w-3 rounded-full bg-[#febc2e] hover:brightness-90 transition-all cursor-pointer flex items-center justify-center"
          title="Restore"
        >
          <Minus className="h-2 w-2 text-[#5f4a00] opacity-0 group-hover/btn:opacity-100 transition-opacity" strokeWidth={3} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 shrink-0 ml-auto">
      <button
        type="button"
        onClick={handleClose}
        className="group/btn h-3 w-3 rounded-full bg-[#ff5f57] hover:brightness-90 transition-all cursor-pointer flex items-center justify-center"
        title={isLastPanel ? 'Clear app' : 'Close panel'}
      >
        <X className="h-2 w-2 text-[#4a0002] opacity-0 group-hover/btn:opacity-100 transition-opacity" strokeWidth={3} />
      </button>
      <button
        type="button"
        onClick={handleMaximize}
        className="group/btn h-3 w-3 rounded-full bg-[#28c840] hover:brightness-90 transition-all cursor-pointer flex items-center justify-center"
        title="Maximize"
      >
        <svg viewBox="0 0 10 10" className="h-1.5 w-1.5 text-[#006500] opacity-0 group-hover/btn:opacity-100 transition-opacity">
          <path d="M0 3.5L5 0L10 3.5V10H0Z" fill="currentColor" />
        </svg>
      </button>
    </div>
  );
};

const MaximizeButton = ({ panelId }: { panelId: string }) => {
  const { maximizedPanelId, setMaximizedPanelId } = useWorkspace();
  const isMaximized = maximizedPanelId === panelId;

  return (
    <div className="flex items-center gap-1.5 shrink-0 ml-auto">
      <button
        type="button"
        onClick={() => setMaximizedPanelId(isMaximized ? null : panelId)}
        className="group/btn h-3 w-3 rounded-full bg-[#28c840] hover:brightness-90 transition-all cursor-pointer flex items-center justify-center"
        title={isMaximized ? 'Restore' : 'Maximize'}
      >
        {isMaximized ? (
          <Minus className="h-2 w-2 text-[#006500] opacity-0 group-hover/btn:opacity-100 transition-opacity" strokeWidth={3} />
        ) : (
          <svg viewBox="0 0 10 10" className="h-1.5 w-1.5 text-[#006500] opacity-0 group-hover/btn:opacity-100 transition-opacity">
            <path d="M0 3.5L5 0L10 3.5V10H0Z" fill="currentColor" />
          </svg>
        )}
      </button>
    </div>
  );
};

type AppMenuEntry = { key: string; name: string; icon: LucideIcon };

type LockedContextMenuProps = {
  panelId: string;
  hasApp: boolean;
  isLastPanel: boolean;
  appEntries?: AppMenuEntry[];
  onSplit: (panelId: string, direction: 'horizontal' | 'vertical') => void;
  onRemove: (panelId: string) => void;
  onClearApp: () => void;
  onOpenApp?: (appType: string) => void;
  children: React.ReactNode;
};

const LockedContextMenu = ({ panelId, hasApp, isLastPanel, appEntries, onSplit, onRemove, onClearApp, onOpenApp, children }: LockedContextMenuProps) => {
  const { maximizedPanelId, setMaximizedPanelId } = useWorkspace();
  const isMaximized = maximizedPanelId === panelId;

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onClick={() => setMaximizedPanelId(isMaximized ? null : panelId)}>
          {isMaximized ? 'Restore' : 'Maximize'}
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={() => onSplit(panelId, 'vertical')}>Split vertical</ContextMenuItem>
        <ContextMenuItem onClick={() => onSplit(panelId, 'horizontal')}>Split horizontal</ContextMenuItem>
        {appEntries && appEntries.length > 0 && onOpenApp && (
          <>
            <ContextMenuSeparator />
            <ContextMenuSub>
              <ContextMenuSubTrigger>Open app</ContextMenuSubTrigger>
              <ContextMenuSubContent>
                {appEntries.map((entry) => (
                  <ContextMenuItem key={entry.key} onClick={() => onOpenApp(entry.key)}>
                    <entry.icon className="h-3.5 w-3.5 mr-2 shrink-0" />
                    {entry.name}
                  </ContextMenuItem>
                ))}
              </ContextMenuSubContent>
            </ContextMenuSub>
          </>
        )}
        <ContextMenuSeparator />
        {hasApp && <ContextMenuItem onClick={onClearApp}>Close app</ContextMenuItem>}
        {!isLastPanel && (
          <ContextMenuItem className="text-red-500 focus:text-red-500" onClick={() => onRemove(panelId)}>
            Close panel
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};

export const PanelSlot = ({ panel, registry, components, interactive, locked, noHeader, isLastPanel, onSetApp, onSplit, onRemove }: PanelSlotProps) => {
  const { maximizedPanelId, transitioningPanelId, isMobile, onMobileBack } = useWorkspace();
  const isMaximized = maximizedPanelId === panel.id;

  const rawPanelComponent = components?.[panel.id];
  const panelEntry = rawPanelComponent && isPanelEntry(rawPanelComponent) ? rawPanelComponent : null;
  const PanelComponent = panelEntry ? panelEntry.component : (rawPanelComponent as ComponentType | undefined);

  const entry = panel.appType ? registry[panel.appType] : null;
  const AppComponent = PanelComponent ?? entry?.component;

  // Resolve header, provider, onClose from PanelComponentEntry or registry
  const HeaderComponent = panelEntry?.header ?? entry?.header;
  const ProviderComponent = panelEntry?.provider ?? entry?.provider;
  const onClose = panelEntry?.onClose;

  const appEntries: AppMenuEntry[] = Object.entries(registry).map(([key, e]) => ({ key, name: e.name, icon: e.icon }));
  const handleOpenApp = (appType: string) => onSetApp(panel.id, appType);

  const contextMenu = interactive
    ? locked
      ? (content: React.ReactNode) => (
          <LockedContextMenu panelId={panel.id} hasApp={!!AppComponent} isLastPanel={isLastPanel} appEntries={appEntries} onSplit={onSplit} onRemove={onRemove} onClearApp={() => onSetApp(panel.id, null)} onOpenApp={handleOpenApp}>{content}</LockedContextMenu>
        )
      : (content: React.ReactNode) => (
          <PanelContextMenu panelId={panel.id} hasApp={!!AppComponent} isLastPanel={isLastPanel} onSplit={onSplit} onRemove={onRemove} onClearApp={() => onSetApp(panel.id, null)}>
            {content}
          </PanelContextMenu>
        )
    : (content: React.ReactNode) => <>{content}</>;

  const overlays = interactive && !locked ? (
    <>
      <SwapOverlay panelId={panel.id} />
      <SwapSourceIndicator panelId={panel.id} />
    </>
  ) : null;

  if (!AppComponent) {
    if (!interactive) {
      return (
        <div className="h-full w-full p-1">
          <div className="h-full w-full rounded-lg border-3 border-primary/50" />
        </div>
      );
    }

    if (locked) {
      return contextMenu(
        <div data-panel-id={panel.id} className="group/panel relative h-full w-full p-1">
          <div className="h-full w-full rounded-lg border-3 border-primary/50 flex items-center justify-center">
            {appEntries.length > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-2 p-4">
                {appEntries.map((entry) => (
                  <button
                    key={entry.key}
                    type="button"
                    onClick={() => handleOpenApp(entry.key)}
                    className="flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                  >
                    <entry.icon className="h-3.5 w-3.5 shrink-0" />
                    {entry.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>,
      );
    }

    return contextMenu(
      <div data-panel-id={panel.id} className="group/panel relative h-full w-full p-1">
        <Card className="relative h-full w-full flex flex-col items-center justify-center gap-3 p-4">
          <span className="text-sm text-muted-foreground">Empty panel</span>
        </Card>
        {overlays}
      </div>,
    );
  }

  if (entry?.transparent) {
    return contextMenu(
      <div data-panel-id={panel.id} className="group/panel relative h-full w-full p-1">
        <div className="relative h-full w-full overflow-hidden">
          <AppComponent panelId={panel.id} />
        </div>
        {overlays}
      </div>,
    );
  }

  // All apps get a chrome header — custom HeaderComponent or default from registry icon+name
  const DefaultHeader = entry ? () => (
    <>
      <entry.icon className="h-3.5 w-3.5 shrink-0" />
      <span className="text-xs font-medium truncate flex-1">{entry.name}</span>
    </>
  ) : null;

  const ResolvedHeader = HeaderComponent ?? DefaultHeader;

  const trafficLights = interactive && !isMobile ? (
    locked ? (
      <MaximizeButton panelId={panel.id} />
    ) : (
      <TrafficLights panelId={panel.id} isLastPanel={isLastPanel} onRemove={onRemove} onClearApp={() => onSetApp(panel.id, null)} />
    )
  ) : null;

  const mobileBackButton = isMobile && onMobileBack ? (
    <button
      type="button"
      onClick={onMobileBack}
      className="flex items-center gap-0.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer shrink-0"
    >
      <ChevronLeft className="h-4 w-4" />
      Back
    </button>
  ) : null;

  const headerContent = (
    <div className="shrink-0 flex items-center gap-2 px-3 py-1.5 border-b text-foreground font-semibold">
      {mobileBackButton}
      {ResolvedHeader && <ResolvedHeader panelId={panel.id} />}
      {!mobileBackButton && onClose && (
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-muted transition-colors cursor-pointer"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {trafficLights}
    </div>
  );

  const headerBar = interactive ? (
    locked ? (
      <LockedContextMenu panelId={panel.id} hasApp={!!AppComponent} isLastPanel={isLastPanel} appEntries={appEntries} onSplit={onSplit} onRemove={onRemove} onClearApp={() => onSetApp(panel.id, null)} onOpenApp={handleOpenApp}>{headerContent}</LockedContextMenu>
    ) : (
      <PanelContextMenu panelId={panel.id} hasApp={!!AppComponent} isLastPanel={isLastPanel} onSplit={onSplit} onRemove={onRemove} onClearApp={() => onSetApp(panel.id, null)}>
        {headerContent}
      </PanelContextMenu>
    )
  ) : headerContent;

  const body = (
    <div className="flex-1 min-h-0">
      <Card className="h-full w-full overflow-hidden p-0 rounded-none border-0 shadow-none">
        <AppComponent panelId={panel.id} />
      </Card>
    </div>
  );

  const inner = ProviderComponent ? (
    <ProviderComponent panelId={panel.id}>
      {!noHeader && headerBar}
      {body}
    </ProviderComponent>
  ) : (
    <>
      {!noHeader && headerBar}
      {body}
    </>
  );

  if (isMaximized) {
    return (
      <>
        {/* Placeholder to preserve layout space */}
        <div data-panel-id={panel.id} className="h-full w-full p-1">
          <div className="h-full w-full rounded-lg border border-dashed border-border/50" />
        </div>
        {/* Maximized overlay — portalled to escape stacking contexts */}
        {createPortal(
          <div className="fixed inset-0 z-50 p-2">
            <div
              className="relative h-full w-full overflow-hidden rounded-lg border backdrop-blur-xl p-2 flex flex-col shadow-2xl bg-background"
              style={{ ...(transitioningPanelId === panel.id ? { viewTransitionName: `panel-${panel.id}` } : {}) } as React.CSSProperties}
            >
              {inner}
            </div>
          </div>,
          document.body,
        )}
      </>
    );
  }

  return (
    <div data-panel-id={panel.id} className="group/panel relative h-full w-full p-1">
      <div
        className="relative h-full w-full overflow-hidden rounded-lg border-2 border-border backdrop-blur-xl p-2 flex flex-col bg-card"
        style={{ ...(transitioningPanelId === panel.id ? { viewTransitionName: `panel-${panel.id}` } : {}) } as React.CSSProperties}
      >
        {inner}
      </div>
      {overlays}
    </div>
  );
};
